import { ClipboardList } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useLocale from '../contexts/LocaleContext';
import { useSurveys } from '../hooks/useSurveys';
import type { SurveyDto } from '../types/api';
import { Button } from './ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/Dialog';

const DISMISS_KEY_PREFIX = 'nhip_survey_reminder_dismissed_';

const localizedTitle = (survey: SurveyDto, locale: string) =>
  locale === 'rw'
    ? survey.titleKinyarwanda || survey.title
    : locale === 'fr'
      ? survey.titleFrench || survey.title
      : survey.title;

/**
 * Auto-appearing reminder for ACTIVE surveys with pending responses in the
 * user's jurisdiction. Shown once per browser session per survey; the CTA
 * forwards straight to the survey page.
 */
export default function SurveyReminderModal() {
  const { locale, dictionary } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: surveys } = useSurveys();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);

  const pendingSurvey = useMemo(() => {
    if (!surveys) return null;
    return (
      surveys.find(
        s =>
          s.status === 'ACTIVE' &&
          (s.expectedCount ?? 0) > 0 &&
          (s.respondedCount ?? 0) < (s.expectedCount ?? 0) &&
          !dismissedIds.has(s.id) &&
          sessionStorage.getItem(`${DISMISS_KEY_PREFIX}${s.id}`) !== 'true'
      ) ?? null
    );
  }, [surveys, dismissedIds]);

  // Don't interrupt users already on the surveys pages
  const onSurveysPage = location.pathname.startsWith('/surveys');

  useEffect(() => {
    setOpen(Boolean(pendingSurvey) && !onSurveysPage);
  }, [pendingSurvey, onSurveysPage]);

  if (!pendingSurvey) return null;

  const dismiss = () => {
    sessionStorage.setItem(`${DISMISS_KEY_PREFIX}${pendingSurvey.id}`, 'true');
    setDismissedIds(prev => new Set(prev).add(pendingSurvey.id));
    setOpen(false);
  };

  const goToSurvey = () => {
    dismiss();
    navigate(`/surveys/${pendingSurvey.id}`);
  };

  const expected = pendingSurvey.expectedCount ?? 0;
  const pending = expected - (pendingSurvey.respondedCount ?? 0);
  const isOwnResponse = expected === 1;

  return (
    <Dialog open={open} onOpenChange={value => (value ? setOpen(true) : dismiss())}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <div className='mx-auto bg-secondary rounded-full p-3 mb-1 w-fit'>
            <ClipboardList className='h-6 w-6 text-primary' />
          </div>
          <DialogTitle className='text-center'>
            {dictionary['surveys.reminder_title'] || 'Survey awaiting response'}
          </DialogTitle>
          <DialogDescription className='text-center'>
            <span className='font-medium text-gray-900'>{localizedTitle(pendingSurvey, locale)}</span>
            <br />
            {isOwnResponse
              ? dictionary['surveys.reminder_message_own'] || 'Your area has not responded to this survey yet.'
              : `${pending} ${dictionary['surveys.reminder_message_pending'] || 'location(s) in your area have not responded yet.'}`}
            {pendingSurvey.dueDate && (
              <>
                <br />
                <span className='text-xs text-gray-500'>
                  {dictionary['surveys.reminder_due'] || 'Due'}: {new Date(pendingSurvey.dueDate).toLocaleDateString()}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='sm:justify-center gap-2'>
          <Button type='button' variant='outline' size='sm' onClick={dismiss}>
            {dictionary['surveys.reminder_later'] || 'Later'}
          </Button>
          <Button type='button' size='sm' onClick={goToSurvey}>
            {dictionary['surveys.reminder_cta'] || 'Respond now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
